<template>
  <div>
    <div class="row mx-0 w-100 mb-0 flex-nowrap flex-grow-1 no-gutters h-auto mb-0 overflow-hidden">
      <Swiper
        :id="sliderId"
        class="w-100 flex-grow-1 h-auto mb-0"
        :class="swiperClass"
        :initial-slide="initialSlide"
        :modules="[Scrollbar]"
        :on-init="onSwiperInit"
        :loop="false"
        :scrollbar="swiperOptions.scrollbar"
        :space-between="10"
        :slides-per-view="swiperOptions.slidesPerView"
        :breakpoints="swiperOptions.breakpoints"
        @swiper="onSwiper"
        @slide-change="onSlideChange"
        @update="() => {
          carouselSwiper.activeIndex = 0;
          currentSlideId = 0;
        }"
      >
        <SwiperSlide
          v-for="slide in slides"
          :key="slide.id"
          class="flex flex-col h-auto"
        >
          <article
            class="product-item product-item--secondary relative flex flex-col flex-grow-1 p-2 sm:p-3 md:p-4 lg:p-0 relative"
          >
            <!-- image -->
            <div
              class="product-item__image embed-responsive mb-4 lg:mb-3"
              :class="{ 'no-padding-height': !slide.images[0]?.has_padding }"
            >
              <RemoteResponsiveImage
                :product-image-data="slide.images[0]?.data"
                :img-class="`embed-responsive-item relative bg-white ${
                  slide.images[0]?.has_padding ? 'image-contain' : 'image-cover p-0'
                }`"
                :alt="slide.images[0]?.alt || slide.images[0]?.label"
                sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
              />
            </div>
            <!-- name + description -->
            <div class="lg:px-4 xl:px-7 lg:text-center text-sm">
              <h3 class="product-item__title mb-0 lg:mb-2 h5 font-medium font-family-base">
                <router-link
                  :to="{ name: 'productPath', params: { permalink: slide.permalink } }"
                  class="no-decoration stretched-link zindex-1"
                >
                  {{ slide.name }}
                </router-link>
              </h3>
              <template v-if="!modest">
                <div class="flex mb-2 mt-2 items-center lg:justify-center">
                  <div
                    v-if="!slide.starRating || (slide.starRating && parseInt(slide.starRating) === 0)"
                    class="md:flex hidden"
                    style="height: 24px;"
                  />
                  <ProductStarsRating
                    v-else
                    :rating="slide.starRating"
                    :rating-sum="slide.starRatingCommentsSum"
                    :show-origin="true"
                    :mobile-minified="true"
                    add-class="xxl:justify-start"
                    theme="light"
                  />
                  <template v-if="slide.commentsSum">
                    <hr
                      v-if="slide.starRating && parseInt(slide.starRating) > 0"
                      class="rating-divider bg-primary mx-2 my-0"
                    >
                    <span>
                      <i class="mi-comment" /> {{ slide.commentsSum }}
                    </span>
                  </template>
                </div>
                <p
                  v-if="slide.positives && showDescText"
                  class="mb-2 hidden lg:block"
                  v-html="slide.positives"
                />
                <p
                  v-else-if="showDescText"
                  class="mb-2 hidden lg:block"
                >
                  {{ slide.description }}
                </p>
              </template>
            </div>
            <!-- price, grammage, add to basket -->
            <div v-if="!viewOnly" class="form-row mt-auto items-center pt-1 lg:pt-0 leading-sm lg:text-center text-nowrap justify-center mx-0">
              <div class="col-12">
                <div class="row justify-center">
                  <!-- price -->
                  <div class="col-12 lg:col-auto">
                    <span class="product-item__price h4 mb-0 font-family-base font-bold">
                      <ProductPrice
                        :price-first="slide.price"
                        :currency="slide.currency"
                      />
                    </span>
                    <!-- grammage -->
                    <span class="product-item__grammage h5 mb-0 font-family-base font-normal text-muted">
                      / {{ slide.unit === 'g' ? slide.weight : slide.volume }} {{ slide.unit }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="col-12 lg:col-7 pt-2 flex flex-col lg:block zindex-2">
                <AddToCartButton
                  v-if="showAddToCartButton"
                  :product="slide"
                  theme="transparent"
                  :odometer-fill="true"
                />
              </div>
            </div>
            <!-- add to favourite -->
            <div v-if="!viewOnly" class="absolute top-0 right-0 p-1 p-0 lg:mr-3 lg:mt-3">
              <Favorite
                :id="slide.id"
                btn-class="lg:p-2 stretched-link bg-white"
              />
            </div>
          </article>
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- Carousel arrows -->
    <div
      v-if="currentSlideId > 0"
      class="absolute top-0 left-0 mt-8 pt-6 xl:pt-8 ml-1 hidden lg:block zindex-11"
    >
      <ButtonChevron
        direction="l"
        class="btn-warning text-primary p-4"
        aria-label="Previous slide"
        @click="() => { carouselSwiper.slidePrev() }"
      />
    </div>
    <div
      v-if="!isEnd"
      class="absolute top-0 right-0 mt-8 pt-6 xl:pt-8 mr-n3 hidden lg:block zindex-11"
    >
      <ButtonChevron
        direction="r"
        class="btn-warning text-primary p-4"
        aria-label="Next slide"
        @click="() => { carouselSwiper.slideNext() }"
      />
    </div>
  </div>
</template>

<script setup>
import { Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { ref } from 'vue'

import ButtonChevron from '@/components/buttons/ButtonChevron.vue'
import AddToCartButton from '@/components/buttons/ButtonAddToCartButton.vue'
import Favorite from '@/components/buttons/ButtonFavorite.vue'
import ProductPrice from "@/components/products/ProductPrice.vue";
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import ProductStarsRating from '@/components/products/ProductStarsRating.vue'

defineProps({
  showAddToCartButton: {
    type: Boolean,
    default: true,
  },

  detailButtonLabel: {
    type: String,
    default: '',
  },

  slides: {
    type: Array,
    default: () => [],
  },

  showDescText: {
    type: Boolean,
    default: true,
  },

  sliderId: {
    type: String,
    default: 'favorite-mixes-wrapper',
  },

  swiperClass: {
    type: String,
    default: ''
  },
  modest: {
    type: Boolean,
    default: false
  },
  viewOnly: {
    type: Boolean,
    default: false
  }
})

const initialSlide = 0
const currentSlideId = ref(0)
const carouselSwiper = ref()
const isEnd = ref(false)

const swiperOptions = {
  slidesPerView: 1,
  scrollbar: {
    draggable: true,
    hide: false
  },
  breakpoints: {
    500: {
      slidesPerView: 2,
    },
    768: {
      slidesPerView: 3,
      spaceBetween: 20,
    },
    1200: {
      spaceBetween: 30,
      slidesPerView: 3,
    },
    1400: {
      spaceBetween: 40,
      slidesPerView: 3,
    }
  },
};

const onSlideChange = () => {
  currentSlideId.value = carouselSwiper.value.activeIndex

  if (carouselSwiper.value.isEnd) {
    isEnd.value = true
  } else isEnd.value = false
}

const onSwiper = (swiper) => {
  carouselSwiper.value = swiper
  carouselSwiper.value.scrollbar?.updateSize()
}
const onSwiperInit = (swiper) => {
  swiper.scrollbar?.updateSize()
}
</script>

<i18n>
  {
    "cs": {
    "add_new_to_cart": "Přidat do košíku",
    "to_cart": "Do košíku"
},
    "sk": {
    "add_new_to_cart": "Pridať do košíka",
    "to_cart": "Do košíka"
},
    "pl": {
    "add_new_to_cart": "Dodaj do koszyka",
    "to_cart": "Do koszyka"
},
    "hu": {
    "add_new_to_cart": "Kosárba",
    "to_cart": "Kosárba"
},
    "de": {
    "add_new_to_cart": "Hinzufügen",
    "to_cart": "Hinzufügen"
},
    "at": {
    "add_new_to_cart": "Hinzufügen",
    "to_cart": "Hinzufügen"
},
    "ro": {
    "add_new_to_cart": "Adaugă în coș",
    "to_cart": "În coș"
},
    "fr": {
    "add_new_to_cart": "Au panier",
    "to_cart": "Au panier"
},
    "it": {
    "add_new_to_cart": "Aggiungi al carrello",
    "to_cart": "Al carrello"
}
  }
</i18n>
